// Simple toast replacement for now
const toast = {
  error: (message) => console.error("Toast Error:", message),
  success: (message) => console.log("Toast Success:", message),
  loading: (message) => console.log("Toast Loading:", message),
};

/**
 * Notification Error Handler Utility
 *
 * Provides comprehensive error handling for notification system
 * with user-friendly messages and recovery mechanisms
 */

// Error types and their configurations
export const ERROR_TYPES = {
  NETWORK_ERROR: "NETWORK_ERROR",
  AUTH_ERROR: "AUTH_ERROR",
  NOT_FOUND_ERROR: "NOT_FOUND_ERROR",
  TIMEOUT_ERROR: "TIMEOUT_ERROR",
  SERVER_ERROR: "SERVER_ERROR",
  CLIENT_ERROR: "CLIENT_ERROR",
  VALIDATION_ERROR: "VALIDATION_ERROR",
  RATE_LIMIT_ERROR: "RATE_LIMIT_ERROR",
  UNKNOWN_ERROR: "UNKNOWN_ERROR",
};

// User-friendly error messages
const ERROR_MESSAGES = {
  [ERROR_TYPES.NETWORK_ERROR]: {
    title: "Connection Problem",
    message: "Please check your internet connection and try again.",
    action: "Retry",
    severity: "warning",
  },
  [ERROR_TYPES.AUTH_ERROR]: {
    title: "Authentication Required",
    message: "Please log in to view your notifications.",
    action: "Login",
    severity: "error",
  },
  [ERROR_TYPES.NOT_FOUND_ERROR]: {
    title: "Not Found",
    message: "The requested notification could not be found.",
    action: "Refresh",
    severity: "info",
  },
  [ERROR_TYPES.TIMEOUT_ERROR]: {
    title: "Request Timeout",
    message: "The request took too long. Please try again.",
    action: "Retry",
    severity: "warning",
  },
  [ERROR_TYPES.SERVER_ERROR]: {
    title: "Server Error",
    message: "Something went wrong on our end. Please try again later.",
    action: "Retry",
    severity: "error",
  },
  [ERROR_TYPES.CLIENT_ERROR]: {
    title: "Request Error",
    message: "There was a problem with your request.",
    action: "Retry",
    severity: "error",
  },
  [ERROR_TYPES.VALIDATION_ERROR]: {
    title: "Invalid Data",
    message: "Please check your input and try again.",
    action: "Fix",
    severity: "warning",
  },
  [ERROR_TYPES.RATE_LIMIT_ERROR]: {
    title: "Too Many Requests",
    message: "Please wait a moment before trying again.",
    action: "Wait",
    severity: "warning",
  },
  [ERROR_TYPES.UNKNOWN_ERROR]: {
    title: "Unexpected Error",
    message: "An unexpected error occurred. Please try again.",
    action: "Retry",
    severity: "error",
  },
};

// Recovery strategies for different error types
const RECOVERY_STRATEGIES = {
  [ERROR_TYPES.NETWORK_ERROR]: {
    retryAttempts: 3,
    retryDelay: [1000, 2000, 4000],
    exponentialBackoff: true,
    showRetryButton: true,
    autoRetry: false,
  },
  [ERROR_TYPES.AUTH_ERROR]: {
    retryAttempts: 0,
    redirectToLogin: true,
    clearState: true,
  },
  [ERROR_TYPES.NOT_FOUND_ERROR]: {
    retryAttempts: 1,
    retryDelay: [1000],
    removeFromState: true,
    showToast: true,
  },
  [ERROR_TYPES.TIMEOUT_ERROR]: {
    retryAttempts: 2,
    retryDelay: [2000, 5000],
    showRetryButton: true,
    autoRetry: false,
  },
  [ERROR_TYPES.SERVER_ERROR]: {
    retryAttempts: 2,
    retryDelay: [3000, 6000],
    exponentialBackoff: true,
    showRetryButton: true,
    autoRetry: false,
  },
  [ERROR_TYPES.CLIENT_ERROR]: {
    retryAttempts: 1,
    retryDelay: [1000],
    showRetryButton: false,
  },
  [ERROR_TYPES.VALIDATION_ERROR]: {
    retryAttempts: 0,
    showValidationErrors: true,
  },
  [ERROR_TYPES.RATE_LIMIT_ERROR]: {
    retryAttempts: 1,
    retryDelay: [30000], // 30 seconds
    showRetryButton: true,
    autoRetry: true,
  },
  [ERROR_TYPES.UNKNOWN_ERROR]: {
    retryAttempts: 1,
    retryDelay: [2000],
    showRetryButton: true,
  },
};

/**
 * Determine error type from error object
 * @param {Error} error - The error object
 * @returns {string} Error type constant
 */
export const getErrorType = (error) => {
  if (!error) return ERROR_TYPES.UNKNOWN_ERROR;

  // Check for explicit error type
  if (error.type && ERROR_TYPES[error.type]) {
    return error.type;
  }

  // Determine type from error properties
  if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
    return ERROR_TYPES.TIMEOUT_ERROR;
  }

  if (!error.response && error.request) {
    return ERROR_TYPES.NETWORK_ERROR;
  }

  if (error.response) {
    const status = error.response.status;

    if (status === 401) return ERROR_TYPES.AUTH_ERROR;
    if (status === 404) return ERROR_TYPES.NOT_FOUND_ERROR;
    if (status === 429) return ERROR_TYPES.RATE_LIMIT_ERROR;
    if (status >= 400 && status < 500) return ERROR_TYPES.CLIENT_ERROR;
    if (status >= 500) return ERROR_TYPES.SERVER_ERROR;
  }

  return ERROR_TYPES.UNKNOWN_ERROR;
};

/**
 * Get user-friendly error message configuration
 * @param {string} errorType - Error type constant
 * @param {Object} context - Additional context for the error
 * @returns {Object} Error message configuration
 */
export const getErrorMessage = (errorType, context = {}) => {
  const baseMessage =
    ERROR_MESSAGES[errorType] || ERROR_MESSAGES[ERROR_TYPES.UNKNOWN_ERROR];

  return {
    ...baseMessage,
    ...context,
  };
};

/**
 * Get recovery strategy for error type
 * @param {string} errorType - Error type constant
 * @returns {Object} Recovery strategy configuration
 */
export const getRecoveryStrategy = (errorType) => {
  return (
    RECOVERY_STRATEGIES[errorType] ||
    RECOVERY_STRATEGIES[ERROR_TYPES.UNKNOWN_ERROR]
  );
};

/**
 * Check if error should be retried
 * @param {string} errorType - Error type constant
 * @param {number} attemptCount - Current attempt count
 * @returns {boolean} Whether to retry
 */
export const shouldRetry = (errorType, attemptCount) => {
  const strategy = getRecoveryStrategy(errorType);
  return attemptCount < (strategy.retryAttempts || 0);
};

/**
 * Get retry delay for error type and attempt
 * @param {string} errorType - Error type constant
 * @param {number} attemptIndex - Current attempt index (0-based)
 * @returns {number} Delay in milliseconds
 */
export const getRetryDelay = (errorType, attemptIndex) => {
  const strategy = getRecoveryStrategy(errorType);

  if (!strategy.retryDelay || strategy.retryDelay.length === 0) {
    return 1000; // Default 1 second
  }

  // Use exponential backoff if configured
  if (strategy.exponentialBackoff && attemptIndex > 0) {
    const baseDelay = strategy.retryDelay[0] || 1000;
    return Math.min(baseDelay * Math.pow(2, attemptIndex), 30000); // Max 30 seconds
  }

  // Use configured delays
  return (
    strategy.retryDelay[attemptIndex] ||
    strategy.retryDelay[strategy.retryDelay.length - 1]
  );
};

/**
 * Show user-friendly error toast
 * @param {Error} error - The error object
 * @param {Object} options - Toast options
 */
export const showErrorToast = (error, options = {}) => {
  const errorType = getErrorType(error);
  const errorMessage = getErrorMessage(errorType, options.context);

  const toastOptions = {
    duration: options.duration || 5000,
    position: options.position || "top-right",
    ...options.toastOptions,
  };

  switch (errorMessage.severity) {
    case "error":
      toast.error(errorMessage.message, toastOptions);
      break;
    case "warning":
      toast(errorMessage.message, {
        ...toastOptions,
        icon: "⚠️",
      });
      break;
    case "info":
      toast(errorMessage.message, {
        ...toastOptions,
        icon: "ℹ️",
      });
      break;
    default:
      toast.error(errorMessage.message, toastOptions);
  }
};

/**
 * Log error to console and external services
 * @param {Error} error - The error object
 * @param {Object} context - Additional context
 */
export const logError = (error, context = {}) => {
  const errorType = getErrorType(error);
  const timestamp = new Date().toISOString();

  const errorLog = {
    type: errorType,
    message: error.message,
    stack: error.stack,
    timestamp,
    url: window.location.href,
    userAgent: navigator.userAgent,
    context,
  };

  // Log to console in development
  if (process.env.NODE_ENV === "development") {
    console.error("Notification Error:", errorLog);
  }

  // Send to error reporting service in production
  if (process.env.NODE_ENV === "production") {
    // Example: Send to error reporting service
    // window.errorReportingService?.captureException(error, {
    //   tags: { component: "NotificationSystem" },
    //   extra: errorLog,
    // });
  }
};

/**
 * Handle notification API errors with comprehensive recovery
 * @param {Error} error - The error object
 * @param {Object} options - Handling options
 * @returns {Object} Error handling result
 */
export const handleNotificationError = (error, options = {}) => {
  const {
    showToast = true,
    logError: shouldLog = true,
    context = {},
    onRetry,
    onAuthError,
    onNotFound,
  } = options;

  const errorType = getErrorType(error);
  const strategy = getRecoveryStrategy(errorType);
  const errorMessage = getErrorMessage(errorType, context);

  // Log error if enabled
  if (shouldLog) {
    logError(error, context);
  }

  // Show toast notification if enabled
  if (showToast && !strategy.redirectToLogin) {
    showErrorToast(error, { context });
  }

  // Handle specific error types
  switch (errorType) {
    case ERROR_TYPES.AUTH_ERROR:
      if (onAuthError) {
        onAuthError(error);
      } else if (strategy.redirectToLogin) {
        // Redirect to login page
        window.location.href = "/login";
      }
      break;

    case ERROR_TYPES.NOT_FOUND_ERROR:
      if (onNotFound) {
        onNotFound(error);
      }
      break;

    default:
      // Handle other error types with retry logic
      if (onRetry && strategy.retryAttempts > 0) {
        onRetry(error, strategy);
      }
      break;
  }

  return {
    errorType,
    errorMessage,
    strategy,
    canRetry: strategy.retryAttempts > 0,
    shouldAutoRetry: strategy.autoRetry,
  };
};

/**
 * Create retry function with exponential backoff
 * @param {Function} operation - The operation to retry
 * @param {Object} options - Retry options
 * @returns {Function} Retry function
 */
export const createRetryFunction = (operation, options = {}) => {
  const {
    maxAttempts = 3,
    baseDelay = 1000,
    exponentialBackoff = true,
    onRetry,
    onError,
  } = options;

  return async (...args) => {
    let lastError;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        return await operation(...args);
      } catch (error) {
        lastError = error;
        const errorType = getErrorType(error);

        // Don't retry certain error types
        if (
          [ERROR_TYPES.AUTH_ERROR, ERROR_TYPES.VALIDATION_ERROR].includes(
            errorType
          )
        ) {
          throw error;
        }

        // Don't retry on last attempt
        if (attempt === maxAttempts - 1) {
          break;
        }

        // Calculate delay
        const delay = exponentialBackoff
          ? Math.min(baseDelay * Math.pow(2, attempt), 30000)
          : baseDelay;

        // Call retry callback
        if (onRetry) {
          onRetry(error, attempt + 1, delay);
        }

        // Wait before retrying
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }

    // All attempts failed
    if (onError) {
      onError(lastError, maxAttempts);
    }

    throw lastError;
  };
};

const errorHandler = {
  ERROR_TYPES,
  getErrorType,
  getErrorMessage,
  getRecoveryStrategy,
  shouldRetry,
  getRetryDelay,
  showErrorToast,
  logError,
  handleNotificationError,
  createRetryFunction,
};

export default errorHandler;
